import { Component, OnInit, ViewChild, ElementRef, NgZone, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import * as _ from 'lodash';

import { ReviewsService } from '../../../../service/reviews.service';
import { AdminService } from '../../../../service/admin.service';
import { EventsService } from '../../../../service/events.service';
import { ReviewsConfigurationService } from '../../../../service/reviews.configuration.service';
import { SubmissionsService } from '../../../../service/submissions.service';
import { UserService } from '../../../../service/user.service';
import { InfoService } from '../../../../service/info.service';
import { NotificationService } from '../../../../service/notification.service';

import { Event } from 'src/app/model/event';
import { Review, SubmissionStatus } from 'src/app/model/paper';
import { User } from 'src/app/model/user';
import { AssignReview } from 'src/app/model/assign.review';
import { Submission } from 'src/app/model/paper';
import { UserEvent } from 'src/app/model/user.event';
import { SelectOption } from 'src/app/model/select.option';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';
import { ReviewStatusPerReviewerList } from 'src/app/model/review.status.list';

import { ReviewStatus } from 'src/app/enum/review.status';
import { Motives, Translations } from 'src/app/enum/assign.motives';

import { UserAssignInfo, SubmissionAssignSuggestions } from './strategy/types';
import { ALGORITHMS, AlgorithmContext, AssignmentAlgorithm } from './strategy/registry';
import { initMaxReviewsPerUser } from './strategy/algorithm';

import { forkJoin, from, Observable } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';
import { PopoverComponent } from 'src/app/component/popover/popover.component';
import { UserEventAnswer } from 'src/app/enum/user.event.answer';
import { TimezoneService } from 'src/app/service/timezone.service';
import { CountryPipe } from 'src/app/pipe/country.pipe';
import { TranslateService } from '@ngx-translate/core';

const ALL = 'ALL';
const WITHOUT_TPC = 'WITHOUT_TPC';
const defaultSelectOption = new SelectOption(ALL, 'admin.event.reviews.assign-suggestion.all');

const defaultAllMembersSelectOption = new SelectOption(ALL, 'admin.event.reviews.assign-suggestion.all-members');
const MAX_NUMBER_OF_CELLS = 1e5;

@Component({
  selector: 'app-assign-review-suggestion',
  templateUrl: './assign-review-suggestion.component.html',
  styleUrls: ['./assign-review-suggestion.component.scss'],
  providers: [CountryPipe],
  // OnPush: os HostListeners de `document:mousemove` do chrome (sidebar/breadcrumb)
  // disparam change detection a cada movimento do mouse. Sem OnPush, o template
  // pesado desta página (matriz N×M + tabela de atribuição) era reverificado a
  // cada mousemove (~250ms → travava). Com OnPush, só reverifica em eventos da
  // própria view e nos markForCheck() dos updates assíncronos abaixo.
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssignReviewSuggestionComponent implements OnInit, HasUnsavedChanges {
  hideMatrix: boolean = false;
  formSelectionFields: FormGroup;
  form: FormGroup;

  // true depois que as atribuições sugeridas foram REALIZADAS (submit bem-sucedido).
  // Enquanto houver sugestões no form e isto for false, há "atribuições pendentes"
  // → o UnsavedChangesGuard bloqueia a saída (ver hasUnsavedChanges/discard).
  private assignmentsSaved = false;

  // Slots (chave `${submissionId}:${control}`) editáveis NESTA rodada — fixado no
  // initForm: vazios (padding) ou sugestões novas (motive CLAIM/INTEREST). Slots de
  // rodada anterior (ASSIGNED_*) ficam travados. NÃO derivamos da motive do valor
  // ATUAL: ao trocar o revisor por um candidato de outra motive, o slot continua
  // editável (senão o botão travava e o popover não reabria).
  private editableSlots = new Set<string>();

  event: Event;
  reviewConfig: EventReviewConfiguration;

  stateColumns = ['id'];
  assignColumns = ['id', 'title', 'reviewer'];

  submissions: Array<Submission>;
  committees: Array<UserEvent>;
  reviewStatusPerReviewerList: Array<ReviewStatusPerReviewerList>;

  matrix: Array<SubmissionAssignSuggestions>;
  // Satisfação média dos membros do TPC (%) do resultado gerado: média, entre os
  // membros que deram ao menos 1 bid, de claims_atendidos / min(bids, carga-alvo).
  // Reflete o algoritmo escolhido → serve p/ comparar as abas. null = ainda não gerado.
  averageSatisfaction: number | null = null;
  // Satisfação do membro MENOS satisfeito (%) — o que o algoritmo justo (min-max) mira.
  minSatisfaction: number | null = null;
  // Linhas RENDERIZADAS da matriz (as duas tabelas iteram por aqui). Fica vazio
  // enquanto o overlay "Preparando…" aparece e recebe TODAS as linhas de uma vez
  // (ver startProgressiveRender): render incremental re-layoutava a tabela sticky
  // a cada lote → O(n²); uma passada só = O(n).
  visibleMatrix: Array<SubmissionAssignSuggestions> = [];
  assignmentStep = 0;

  // Opções do dropdown de algoritmo (a partir do registro ALGORITHMS): value =
  // índice em ALGORITHMS, label = chave i18n (traduzida no select). O VALUE dirige o
  // dispatch em assignSuggestion (por índice) — a EXIBIÇÃO é independente. `.reverse()`
  // = decisão de PRODUTO: "Satisfação justa dos bids" (JEMS3) primeiro/default, "Maior
  // afinidade primeiro" (JEMS1/2) depois. (2 algoritmos = swap; revisar se a lib crescer.)
  algorithmOptions: SelectOption[] = ALGORITHMS.map((a, i) => new SelectOption(i, a.labelKey)).reverse();
  link: { label: string; route: string; align: string; };

  // Chave i18n da explicação amigável do algoritmo escolhido no dropdown — exibida
  // ao TPC Chair (linguagem não-técnica). Segue o form control `algorithm` (índice
  // em ALGORITHMS), então muda ao vivo conforme a seleção.
  get selectedAlgorithmDescriptionKey(): string {
    const idx = this.formSelectionFields?.get('algorithm')?.value ?? 0;
    return (ALGORITHMS[idx] || ALGORITHMS[0]).descriptionKey;
  }

  MotivesPreviously = [ Motives.ASSIGNED_CLAIMED, Motives.ASSIGNED_INTEREST ];
  MotivesAssignSuggested = [ Motives.CLAIM, Motives.INTEREST ];
  MotivesSubmissionStatus = [ Motives.REQUESTED, Motives.AUTHOR, Motives.CONFLICT, Motives.DECLINED ];

  translations = Translations;
  motives = Motives;

  tracks: Array<SelectOption> = [];
  tpcGroups: Array<SelectOption> = [];
  paperGroups: Array<SelectOption> = [];

  reviewsDueAt: Date;
  loadingData: boolean = false;

  constructor(
    private adminService: AdminService,
    public reviewsService: ReviewsService,
    public reviewConfigurationService: ReviewsConfigurationService,
    public submissionsService: SubmissionsService,
    public eventService: EventsService,
    public userService: UserService,
    public infoService: InfoService,
    public notificationService: NotificationService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private tzService: TimezoneService,
    private countryPipe: CountryPipe,
    private ngZone: NgZone,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  @ViewChild('matrixTip') private matrixTip?: ElementRef<HTMLElement>;

  // Seletor de revisores COMPARTILHADO (um único popover p/ toda a tabela, aberto
  // sob demanda e ancorado à célula clicada). Substitui o antigo texto-combobox +
  // mat-select por-célula. Conteúdo lazy → zero custo no load.
  @ViewChild('reviewerPicker') private reviewerPicker?: PopoverComponent;
  pickerContext: { sub: SubmissionAssignSuggestions; control: string } | null = null;
  pickerSelectedId: any = undefined;
  // Id do membro que o ALGORITMO sugeriu para o slot aberto (= suggestedAssign no
  // índice do slot). Só o badge DELE é colorido na lista — não todos que deram
  // bid/interesse.
  pickerSuggestedId: any = undefined;
  pickerCandidates: Array<{ id: any; name: string; affiliation: string; weight: number; motive: string; load: string; loadTotal: number }> = [];
  pickerFilteredCandidates: Array<{ id: any; name: string; affiliation: string; weight: number; motive: string; load: string; loadTotal: number }> = [];

  private boundScrollEl?: HTMLElement;
  private resizeCrosshair?: () => void;   // (re)dimensiona o canvas do crosshair + mede a altura do cabeçalho; chamado no fim do render e em resize (definido na closure do setter)
  private colGeom: Array<{ left: number; width: number }> = [];   // left/width REAIS de cada coluna (medidos 1x no fim do render) — larguras variam (padding/box-sizing), rem fixo não serve
  private rowGeom: Array<{ top: number; height: number }> = [];   // top/height REAIS de cada linha (idem) p/ a faixa HORIZONTAL do crosshair
  matrixRendering = false;   // true durante o render progressivo → overlay "Preparando…" na matriz (o hover/faixa só funciona após o colGeom ser medido, no fim do render)
  // Último estado das bordas de scroll da matriz (sombra header/coluna + fades
  // direita/baixo). Guardado p/ só mexer no DOM quando um estado MUDA.
  private edgeState = { y: false, x: false, right: false, down: false };

  // Recalcula sombras (header/coluna) e fades (direita/baixo) a partir da
  // geometria do scroller. Leituras de layout agrupadas (1 reflow), escritas de
  // classe só depois. Chamado no scroll (rAF), no estado inicial e a cada lote do
  // render progressivo (as linhas crescem por frame → o overflow inferior muda).
  private refreshMatrixEdges(): void {
    const el = this.boundScrollEl;
    if (!el) { return; }
    const vp = el.parentElement;   // .matrix-viewport (ancora os fades)
    const y = el.scrollTop > 0;
    const x = el.scrollLeft > 0;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    const s = this.edgeState;
    if (y !== s.y) { s.y = y; el.classList.toggle('is-scrolled', y); }
    if (x !== s.x) { s.x = x; el.classList.toggle('is-scrolled-x', x); }
    if (right !== s.right) { s.right = right; vp?.classList.toggle('can-scroll-right', right); }
    if (down !== s.down) { s.down = down; vp?.classList.toggle('can-scroll-down', down); }
  }

  // Alterna .is-scrolled (vertical) e .is-scrolled-x (horizontal) no container da
  // matriz, para o header sticky ganhar sombra inferior e a coluna do id ganhar
  // sombra na borda direita. Listener fora da zona Angular (o toggle é só DOM)
  // para não disparar change detection a cada evento de scroll.
  @ViewChild('matrixScroll') set matrixScrollRef(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement;
    if (!el || el === this.boundScrollEl) { return; }
    this.boundScrollEl = el;
    this.ngZone.runOutsideAngular(() => {
      // Ler scrollTop/scrollLeft força reflow (caro no DOM gigante + sticky).
      // Fazemos no MÁXIMO 1x por frame (rAF) e só alternamos a classe da sombra
      // quando o estado de "rolado" MUDA — não a cada frame.
      let scrollScheduled = false;
      el.addEventListener('scroll', () => {
        if (scrollScheduled) { return; }
        scrollScheduled = true;
        requestAnimationFrame(() => {
          scrollScheduled = false;
          this.refreshMatrixEdges();
          // Crosshair é desenhado em coords de viewport → ao rolar, a coluna/linha
          // realçada se move sob ele. Cacheia o scroll (1 leitura/frame) e redesenha
          // — mantém o crosshair alinhado durante o scroll, sem mexer o mouse.
          scrollX = el.scrollLeft; scrollY = el.scrollTop;
          drawCrosshair();
        });
      }, { passive: true });

      // Estado inicial dos fades/sombras assim que o container aparece (após o
      // 1º layout). O render é progressivo, então startProgressiveRender também
      // chama refreshMatrixEdges a cada lote (o overflow inferior muda ao crescer).
      requestAnimationFrame(() => this.refreshMatrixEdges());

      // Um ÚNICO listener de mouseover delegado (fora da zona) faz duas coisas ao
      // trocar de célula:
      //  1) Realce do NOME do revisor no header (.col-hover) — IMEDIATO (só troca
      //     de classe, barato). Em CSS exigiria :has() por coluna (o vilão antigo).
      //  2) Tooltip PRÓPRIO (um <div> fixo estilizado como o mat-tooltip via
      //     .sg-crit-tooltip) — só aparece ao REPOUSAR (SHOW_DELAY); texto do
      //     data-tip. NÃO usa overlay do CDK: só escreve estilo/texto no <div>
      //     (sem anexar ao body, sem ler layout depois de escrever) → zero reflow
      //     forçado, zero zona. O MatTooltip forçava reflow do documento inteiro
      //     (~130ms) a cada show num DOM gigante.
      const tipEl = this.matrixTip?.nativeElement;
      const SHOW_DELAY = 100;
      let restTimer: ReturnType<typeof setTimeout> | null = null;
      let currentCell: HTMLElement | null = null;
      let hoveredTh: HTMLElement | null = null;
      let hoveredRowIdx = -1;
      let hoveredIdCell: HTMLElement | null = null;   // célula do id sob o cursor (negrito via classe, não :hover)
      // Crosshair via CANVAS (não mais faixas no DOM): um único <canvas> viewport-
      // sized em .matrix-viewport. Redesenhar 2 retângulos no bitmap por movimento é
      // um paint LOCAL do canvas e NÃO dispara recálculo de layer-tree/overlap no DOM
      // gigante — que era o custo REAL (~4s). Mover um LAYER do DOM (a faixa) sobre
      // 498k células força o Chrome a re-testar sobreposição na tabela inteira a cada
      // movimento, INDEPENDENTE do tamanho da faixa; por isso viewport-sizing a faixa
      // não ajudou. O canvas é UM elemento fixo: mudar seu bitmap não toca o DOM.
      const vpEl = el.parentElement;   // .matrix-viewport
      const canvas = vpEl?.querySelector<HTMLCanvasElement>('.crosshair-canvas') ?? null;
      const ctx = canvas?.getContext('2d') ?? null;
      let tipShown = false;
      let mouseX = 0, mouseY = 0;   // posição do ponteiro (p/ posicionar o tooltip sem ler layout)
      // Scroll cacheado (rAF) — o crosshair é desenhado em coords de VIEWPORT
      // (= geometria de conteúdo − scroll). Geometria da banda mostrada (ou -1).
      let scrollX = 0, scrollY = 0;
      let shownColLeft = -1, shownColWidth = 0;
      let shownRowTop = -1, shownRowHeight = 0;
      let headerH = 0, canvasW = 0, canvasH = 0;   // headerH: p/ a banda vertical não invadir o thead sticky

      // Desenha as duas bandas no canvas (clear + 2 fillRect). Chamado 1× por troca
      // de célula e no scroll — nunca faz leitura de layout.
      const drawCrosshair = () => {
        if (!ctx) { return; }
        ctx.clearRect(0, 0, canvasW, canvasH);
        const dark = !!el.closest('.dark-theme');
        // Véu MAIS CLARO no dark (0.26 vs 0.07 do light): sobre o fundo preto da matriz
        // o véu claro precisa de mais opacidade p/ ficar visível. Na interseção os dois
        // retângulos somam alpha → a célula sob o cursor destaca um pouco mais.
        ctx.fillStyle = dark ? 'rgba(242, 241, 238, 0.26)' : 'rgba(0, 0, 0, 0.07)';
        // Vertical (coluna): começa ABAIXO do cabeçalho p/ não invadir os nomes.
        if (shownColLeft >= 0) { ctx.fillRect(shownColLeft - scrollX, headerH, shownColWidth, canvasH - headerH); }
        // Horizontal (linha): largura total (cruza a coluna do id, como antes). Na
        // interseção os dois retângulos somam alpha → célula sob o cursor mais escura.
        if (shownRowTop >= 0) { ctx.fillRect(0, shownRowTop - scrollY, canvasW, shownRowHeight); }
      };
      // Dimensiona o canvas ao viewport (backing store em DPR p/ nitidez) e mede a
      // altura do cabeçalho. Só em resize/fim-do-render (nunca por movimento).
      this.resizeCrosshair = () => {
        if (!canvas || !ctx) { return; }
        const dpr = window.devicePixelRatio || 1;
        canvasW = el.clientWidth; canvasH = el.clientHeight;
        canvas.style.width = canvasW + 'px';
        canvas.style.height = canvasH + 'px';
        canvas.width = Math.round(canvasW * dpr);
        canvas.height = Math.round(canvasH * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const thead = el.querySelector<HTMLElement>('.table-weight thead');
        headerH = thead ? thead.getBoundingClientRect().height : 0;
        drawCrosshair();
      };
      const ro = new ResizeObserver(() => this.resizeCrosshair?.());
      ro.observe(el);

      const clearTimer = () => { if (restTimer) { clearTimeout(restTimer); restTimer = null; } };
      const hideColStrip = () => { shownColLeft = -1; };
      const hideRowStrip = () => { shownRowTop = -1; };
      const hideTip = () => { if (tipShown && tipEl) { tipShown = false; tipEl.classList.remove('is-visible'); } };
      const onLeave = () => {
        clearTimer();
        currentCell = null;
        hoveredRowIdx = -1;
        if (hoveredTh) { hoveredTh.classList.remove('col-hover'); hoveredTh = null; }
        hoveredIdCell?.classList.remove('id-hover'); hoveredIdCell = null;
        hideColStrip();
        hideRowStrip();
        drawCrosshair();   // limpa o canvas
        hideTip();
      };

      el.addEventListener('mouseover', (ev: MouseEvent) => {
        mouseX = ev.clientX; mouseY = ev.clientY;   // ponteiro p/ o tooltip (sem leitura de layout)
        const cell = (ev.target as HTMLElement).closest('td') as HTMLElement | null;
        if (cell === currentCell) { return; }   // mesma célula (ex.: entrou no <span> filho)
        currentCell = cell;

        // (1) Realce do nome do revisor no header + células da coluna — imediato.
        const th = cell
          ? (cell.closest('table')?.tHead?.rows[0]?.cells[(cell as HTMLTableCellElement).cellIndex] as HTMLElement | undefined)
          : undefined;
        if (th !== hoveredTh) {
          hoveredTh?.classList.remove('col-hover');
          hoveredTh = th ?? null;
          hoveredTh?.classList.add('col-hover');

          // Banda vertical (coluna) no crosshair: geometria REAL medida (colGeom,
          // left/width por coluna). O desenho subtrai o scroll (coords de viewport).
          // Pula a coluna do id (cellIndex 0). O draw acontece 1× após o bloco da linha.
          const idx = cell ? (cell as HTMLTableCellElement).cellIndex : 0;
          const g = idx > 0 ? this.colGeom[idx] : undefined;
          if (g) { shownColLeft = g.left; shownColWidth = g.width; } else { hideColStrip(); }
        }

        // (2) Tooltip — reinicia; mostra só ao repousar. Montado SOB DEMANDA:
        // mapeia a <td> de volta pro dado (linha = sectionRowIndex no tbody;
        // coluna = cellIndex-1, já que a col 0 é o id) e traduz só a célula sob o
        // ponteiro — em vez dos ~498k tooltips pré-gerados.
        clearTimer();
        hideTip();
        const colIdx = cell ? (cell as HTMLTableCellElement).cellIndex : 0;
        const rowIdx = cell ? (cell.parentElement as HTMLTableRowElement).sectionRowIndex : -1;
        // Faixa HORIZONTAL (linha) — par da vertical (crosshair). Reposiciona só ao
        // MUDAR de linha; geometria medida (rowGeom), sem ler layout por movimento.
        // Aparece também na coluna do id (rowIdx>=0 independe de colIdx).
        if (rowIdx !== hoveredRowIdx) {
          hoveredRowIdx = rowIdx;
          const rg = rowIdx >= 0 ? this.rowGeom[rowIdx] : undefined;
          if (rg) { shownRowTop = rg.top; shownRowHeight = rg.height; } else { hideRowStrip(); }
          // Negrito do id via CLASSE (não :hover): invalida só a célula do id, em vez
          // de o `tr:hover` recalcular a linha inteira (623 células) por troca.
          hoveredIdCell?.classList.remove('id-hover');
          hoveredIdCell = ((cell?.parentElement as HTMLTableRowElement | null)?.cells?.[0] as HTMLElement) ?? null;
          hoveredIdCell?.classList.add('id-hover');
        }
        // 1 desenho por troca de célula (o mouseover já retorna cedo p/ a mesma célula).
        drawCrosshair();
        const cellData = (cell && colIdx > 0 && rowIdx >= 0)
          ? this.visibleMatrix[rowIdx]?.possiblesAssign[colIdx - 1] : undefined;
        if (!cell || !cellData || !tipEl) { return; }
        restTimer = setTimeout(() => {
          restTimer = null;
          // Posiciona pelo PONTEIRO (clientX/Y capturado no mouseover), NÃO pelo
          // getBoundingClientRect da célula — assim NÃO há leitura de layout, logo
          // NENHUM reflow forçado (era isso que custava ~130ms num DOM gigante).
          tipEl.textContent = this.translate.instant(
            'admin.event.reviews.assign-suggestion.weight-tooltip',
            { name: cellData.userPreferences.user.name, weight: cellData.weight }
          );
          tipEl.style.left = mouseX + 'px';
          tipEl.style.top = mouseY + 'px';
          tipShown = true;
          tipEl.classList.add('is-visible');
        }, SHOW_DELAY);
      }, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
    });
  }

  // Espelha paper-info.formatAuthorAffiliation: monta "(SIGLA, País)" a partir
  // do profile do autor. `acronym` é getter do model Affiliation (cai p/ `name`
  // quando vazio), mas só existe se a afiliação vier como instância de classe;
  // para payloads planos, repetimos o fallback p/ `name`.
  formatAuthorAffiliation(author: any): string {
    const profile = author?.user?.profile;
    if (!profile) { return ''; }
    const acronym = profile.affiliation?.acronym || profile.affiliation?.name;
    const country = profile.country ? this.countryPipe.transform(profile.country) : '';
    const parts = [acronym, country].filter(v => !!v);
    return parts.length ? `(${parts.join(', ')})` : '';
  }

  ngOnInit(): void {
    // O form de configuração NÃO depende de fetch (só literais + algorithmOptions
    // estático), então o criamos JÁ: o card + botão "Gerar sugestões" aparecem na
    // hora, independentemente do tamanho do evento. Antes o form só nascia dentro
    // do subscribe do forkJoin (groups/tpc ~1,6s) → o card esperava esse fetch.
    // Os SELETORES de track/grupo populam depois (gated por *ngIf).
    this.initFormSelectionFields();
    this.adminService.progressBar.start();
    setTimeout(() => {
      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.loadSelectionFieldsData();
        });
      } else {
        this.loadSelectionFieldsData();
      }
    });
  }

  loadSelectionFieldsData() {
    this.adminService.progressBar.start();
    forkJoin([
      this.eventService.getEventPaperGroups(this.event.id),
      // 'minimum' = só id/name/event (sem a lista de membros+submissões por grupo
      // que o serializer cheio traz). O seletor usa apenas id+name; os membros vêm
      // depois, no "Gerar sugestões" (getEventCommitteeByTPCGroup). Corta ~350KB.
      this.eventService.getEventTPCGroups(this.event.id, 'minimum'),
    ]).subscribe( ([paperGroup, tpcGroups]) => {
      this.tracks = [defaultSelectOption, ...this.event.tracks.map(track => new SelectOption(track, track.name))];
      this.paperGroups = [new SelectOption(ALL, 'admin.event.reviews.assign-suggestion.all-submissions'),
                          ...paperGroup.map( group => new SelectOption(group, group.name))];

      this.tpcGroups = [defaultAllMembersSelectOption,
                        new SelectOption(WITHOUT_TPC, 'admin.event.reviews.assign-suggestion.without-tpc'),
                        ...tpcGroups.map(group => new SelectOption(group, group.name))];

      // Form já foi criado no ngOnInit (não recriar — perderia estado); aqui só
      // populamos os seletores.
      this.adminService.progressBar.stop();
      this.cdr.markForCheck();
    });
  }

  initFormSelectionFields() {
    this.formSelectionFields = this.fb.group({
      tpcGroup: [defaultSelectOption.id, []],
      track: [defaultSelectOption.id, []],
      paperGroup: [defaultSelectOption.id, []],
      algorithm: [this.algorithmOptions[0].id, []],   // default = 1ª opção exibida (Satisfação justa dos bids); value = índice em ALGORITHMS
      positiveMatchWeight: [1, [Validators.required]],
      negativeMatchWeight: [1, [Validators.required]]
    })
  }

  setSelectFieldsOptions(params) {
    if (this.formSelectionFields.value.paperGroup !== defaultSelectOption.id) {
      params['paperGroup'] = this.formSelectionFields.value.paperGroup.id;
    }

    if (this.formSelectionFields.value.track !== defaultSelectOption.id) {
      params['track'] = this.formSelectionFields.value.track.id;
    }

    return params;
  }

  loadData() {
    this.adminService.progressBar.start();
    this.link = {
      label: 'forms.buttons.assign-review-date',
      route: `/admin/${this.event.id}/reviews/configuration`,
      align: 'end'
    };

    const params = this.setSelectFieldsOptions({ detailed: false, toAssign: true, status: SubmissionStatus.ACTIVE });

    let observableMembersOfTPCGroup: Observable<UserEvent[]>;
    if (this.formSelectionFields.value.tpcGroup === WITHOUT_TPC) {
      observableMembersOfTPCGroup = this.adminService.eventsService.getEventPeopleWithoutTPCGroup(this.event.id);
    } else {
      if (this.formSelectionFields.value.tpcGroup === defaultSelectOption.id) {
        observableMembersOfTPCGroup = this.adminService.eventsService.getEventCommittee(this.event.id, UserEventAnswer.ACCEPTED);
      } else {
        observableMembersOfTPCGroup = this.adminService.eventsService.getEventCommitteeByTPCGroup(this.event.id, UserEventAnswer.ACCEPTED, this.formSelectionFields.value.tpcGroup.id);
      }
    }
    this.loadingData=true;
    forkJoin([
      this.reviewConfigurationService.getReviewsConfiguration(this.event.id),
      this.submissionsService.getSubmissionsByEvent(this.event.id, params),
      this.reviewsService.getReviewStatusPerReviewerListByEvent(this.event.id),
      observableMembersOfTPCGroup
    ]).subscribe(([reviewConfig, submissions, reviewStatusPerReviewer, committees]) => {
      const numberOfCells=submissions.length * committees.length;
      this.hideMatrix = numberOfCells > MAX_NUMBER_OF_CELLS;
      this.reviewConfig = reviewConfig;
      this.submissions = submissions;
      this.reviewStatusPerReviewerList = reviewStatusPerReviewer;
      // Colunas da matriz (membros do TPC) em ordem ALFABÉTICA por nome (pt, ignora
      // acento/caixa; desempate por id). O desempate do algoritmo agora é aleatório,
      // então a ordem dos committees não afeta a distribuição — é só exibição.
      this.committees = committees.map(c => <UserEvent>c)
        .sort((a, b) => a.user.name.localeCompare(b.user.name, 'pt', { sensitivity: 'base' }) || (a.user.id - b.user.id));

      // remove deleted reviews.
      this.submissions.forEach( sub => {
        sub.reviews = sub.reviews.filter(r => (<Review>r).status !== ReviewStatus.DELETED
        );
      });

      this.stateColumns.push(...this.committees.map(c => c.user.id.toString()));

      this.assignSuggestion();
      this.initForm();
      this.adminService.progressBar.stop();
      this.cdr.markForCheck();
      if(this.hideMatrix){ // If it was calculated and the number of cells is above a certain threshold
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          data: {
            title: 'admin.event.reviews.assign-suggestion.matrix-too-big-warn.title',
            content: 'admin.event.reviews.assign-suggestion.matrix-too-big-warn.content',
            confirm:'admin.event.reviews.assign-suggestion.matrix-too-big-warn.confirm',
            cancel: 'admin.event.reviews.assign-suggestion.matrix-too-big-warn.cancel',
            bigButtons:true,
            params:{
              content:{
                members:committees.length,
                submissions:submissions.length
              }
            }
          }
        });
        dialogRef.afterClosed().subscribe(confirmed =>{
          if(!confirmed){
            this.showSecondWarning(numberOfCells);
          }
        });
      }
    });
  }

  showSecondWarning(numberOfCells: number): void{
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.reviews.assign-suggestion.matrix-too-big-final-warn.title',
        content: 'admin.event.reviews.assign-suggestion.matrix-too-big-final-warn.content',
        confirm:'admin.event.reviews.assign-suggestion.matrix-too-big-final-warn.confirm',
        cancel: 'admin.event.reviews.assign-suggestion.matrix-too-big-final-warn.cancel',
        bigButtons:true,
        params:{
          content:{
            numberOfCells: numberOfCells
          }
        }
      }
    });
    dialogRef.afterClosed().subscribe(confirmed =>{
      if(confirmed){
        this.hideMatrix=false;
        this.startProgressiveRender();
      }
    });
  }

  assignSuggestion() {
    // Despacha o algoritmo selecionado (aba). A matriz GENÉRICA é montada aqui
    // (igual p/ todo algoritmo); o algoritmo calcula pesos + atribuição.
    const algorithm = ALGORITHMS[this.formSelectionFields.get('algorithm')?.value] || ALGORITHMS[0];
    const ctx: AlgorithmContext = {
      numberOfCommittees: this.committees.length,
      numberOfReviewsRequired: this.reviewConfig.numberOfReviewsRequired,
    };

    this.matrix = algorithm.run(this.buildMatrix(), ctx, this.readAlgorithmParams(algorithm));
    this.loadingData=false;

    // As células da matriz iteram `possiblesAssign`; as colunas iteram `committees`
    // (já em ordem alfabética). O algoritmo (correctOrder) deixa possiblesAssign por
    // id — reordenamos pelo MESMO critério (nome + id) p/ célula e cabeçalho alinharem.
    // possiblesAssign na MESMA ordem alfabética das colunas. `committees` já vem
    // ordenado (localeCompare uma vez, em loadData), então aqui usamos um ÍNDICE
    // pré-computado (comparação de INTEIROS) em vez de refazer localeCompare por
    // comparação — antes eram ~4,6M chamadas de localeCompare('pt') (800×623) =
    // ~5s no evento grande. Mesma ordem final, custo em milissegundos.
    const committeeOrder = new Map(this.committees.map((c, i) => [c.user.id, i]));
    this.matrix.forEach(sub => sub.possiblesAssign.sort((a, b) =>
      (committeeOrder.get(a.userPreferences.user.id) ?? 0) - (committeeOrder.get(b.userPreferences.user.id) ?? 0)));

    this.precomputeCellDisplay();
    this.startProgressiveRender();
  }

  // Recalcula satisfação média/mínima e dispara CD (OnPush). Chamado após montar o
  // form (valor inicial) e a cada edição manual de revisor (updateSelectControl) ou
  // descarte (clearPendingAssignments) → a linha reflete o estado corrente ao vivo.
  private refreshSatisfaction(): void {
    const stats = this.computeSatisfactionStats();
    this.averageSatisfaction = stats ? stats.avg : null;
    this.minSatisfaction = stats ? stats.min : null;
    this.cdr.markForCheck();
  }

  // Ids que revisam a submissão no estado CORRENTE: revisões válidas já existentes +
  // os slots preenchidos NESTA rodada (lidos do form, não do suggestedAssign — assim
  // reflete as trocas manuais no picker). Set → sem dupla contagem.
  private currentAssignedIds(sub: SubmissionAssignSuggestions): Set<number> {
    const assigned = new Set<number>();
    sub.submission.reviews.forEach(r => {
      const rev = <Review>r;
      if (![ReviewStatus.DELETED, ReviewStatus.DECLINED].some(s => s === rev.status)) {
        assigned.add(rev.userId);
      }
    });
    const group = this.form?.get(sub.submission.id.toString());
    if (group) {
      Object.values(group.value).forEach(v => { if (v) { assigned.add(Number(v)); } });
    }
    return assigned;
  }

  // Satisfação dos membros do TPC (%), do estado CORRENTE das atribuições.
  // Satisfação de um membro = claims_atendidos / min(bids, carga-alvo): claims_atendidos
  // = papers em que ele deu bid E é revisor agora (existente OU slot preenchido);
  // carga-alvo = mMax (teto de revisões por membro), que desconta o limite de carga
  // (não dá p/ atender mais bids do que revisões que fará). Só conta membros com ≥1 bid.
  // Retorna a MÉDIA e o MÍNIMO (o membro menos satisfeito — o que o algoritmo justo mira).
  private computeSatisfactionStats(): { avg: number; min: number } | null {
    if (!this.matrix || !this.committees || !this.reviewConfig) { return null; }

    const [, mMax] = initMaxReviewsPerUser(
      this.matrix.length, this.committees.length, this.reviewConfig.numberOfReviewsRequired);
    const committeeIds = new Set(this.committees.map(c => c.user.id));

    const claims = new Map<number, number>();     // uid → nº de bids (só membros do comitê)
    const satisfied = new Map<number, number>();  // uid → nº de bids atendidos

    this.matrix.forEach(sub => {
      const assigned = this.currentAssignedIds(sub);
      sub.submission.userClaims.forEach(u => {
        const uid = typeof u === 'number' ? u : u.id;
        if (!committeeIds.has(uid)) { return; }   // bids de não-membros não contam
        claims.set(uid, (claims.get(uid) || 0) + 1);
        if (assigned.has(uid)) { satisfied.set(uid, (satisfied.get(uid) || 0) + 1); }
      });
    });

    let sum = 0;
    let members = 0;
    let min = Infinity;
    claims.forEach((total, uid) => {
      const achievable = mMax > 0 ? Math.min(total, mMax) : total;   // total ≥ 1 → achievable ≥ 1
      const s = (satisfied.get(uid) || 0) / achievable;
      sum += s;
      members++;
      if (s < min) { min = s; }
    });

    return members > 0 ? { avg: (sum / members) * 100, min: min * 100 } : null;
  }

  // Lê os valores dos parâmetros do algoritmo a partir do reactive form, pela
  // spec `params` (desacopla o algoritmo da estrutura do form).
  private readAlgorithmParams(algorithm: AssignmentAlgorithm): Record<string, number> {
    const values: Record<string, number> = {};
    algorithm.params.forEach(p => {
      values[p.name] = this.formSelectionFields.get(p.name)?.value;
    });
    return values;
  }

  private startProgressiveRender(): void {
    // Render de TODAS as linhas de UMA VEZ (uma única passada de layout). O render
    // incremental (lote por rAF) re-layoutava a tabela — que tem header e coluna-id
    // STICKY — a cada lote sobre um DOM que cresce → O(n²) (medido ~20s no evento
    // grande). Uma passada só = O(n). O overlay "Preparando…" (matriz vazia) é
    // pintado ANTES do render pesado (os 2 rAF garantem o paint), então a tela
    // mostra o aviso durante o freeze, em vez de congelar em branco.
    this.matrixRendering = true;
    this.visibleMatrix = [];   // overlay (matriz vazia) renderiza no CD do subscribe
    // 2 rAF garantem que o overlay PINTA antes do render pesado; então coloca TODAS
    // as linhas de uma vez (markForCheck → 1 CD = 1 layout, não detectChanges — o CD
    // síncrono reentrava e pegava `event` num instante ruim → erro no template).
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.visibleMatrix = this.matrix;
      this.cdr.markForCheck();
      requestAnimationFrame(() => {
        // 1. Only calculate crosshairs & table-weight geometry if hideMatrix is false
        if (!this.hideMatrix) {
          this.refreshMatrixEdges();
          const el = this.boundScrollEl;
          if (!el) { return; }
          // As faixas de crosshair vivem em .matrix-viewport com altura/largura de 100%
          // do VIEWPORT (CSS) — não mais o tamanho do conteúdo (que estourava a textura
          // da GPU em Retina). A dimensão curta vem por scale no hover; a longa é o CSS.
          // Geometria REAL de COLUNAS e LINHAS (leitura de layout em LOTE): larguras/
          // alturas variam (padding/box-sizing) → valor fixo desalinha. Coords do
          // CONTEÚDO (independem do scroll atual, pois as faixas rolam junto).
          const scrollRect = el.getBoundingClientRect();
          const baseX = scrollRect.left - el.scrollLeft;
          const baseY = scrollRect.top - el.scrollTop;
          this.colGeom = Array.from(el.querySelectorAll<HTMLElement>('.table-weight thead tr th')).map(th => {
            const r = th.getBoundingClientRect();
            return { left: r.left - baseX, width: r.width };
          });
          this.rowGeom = Array.from(el.querySelectorAll<HTMLElement>('.table-weight tbody tr')).map(tr => {
            const r = tr.getBoundingClientRect();
            return { top: r.top - baseY, height: r.height };
          });
          // Dimensiona o canvas do crosshair ao viewport e mede a altura do cabeçalho
          // (agora que o thead tem o tamanho final) — o hover desenha nele.
          this.resizeCrosshair?.();
        }
        // Matriz montada + geometria medida → tudo interativo: some o aviso.
        this.matrixRendering = false;
        this.cdr.markForCheck();
      });
    }));
  }

  // Pré-computa, UMA vez após montar a matriz, os campos de exibição de cada
  // célula: assim o template não chama hasClaim() nem o pipe translate impuro
  // por célula a cada change detection (matriz é N×M — milhares de células).
  // Rodar aqui é seguro: motive/weight já estão finais após a sugestão, e a
  // matriz só muda quando é regerada (que chama isto de novo).
  private precomputeCellDisplay(): void {
    this.matrix.forEach(sub => {
      sub.possiblesAssign.forEach(cell => {
        cell.requested = cell.submission.hasClaim(cell.userPreferences.user.id);
        cell.cssClasses = [cell.motive, cell.requested ? 'REQUESTED' : ''].filter(Boolean).join(' ');
        // tooltip NÃO é mais pré-computado aqui (eram ~498k translate.instant): é
        // montado sob demanda no hover (mouseover handler no ngAfterViewInit).
      });
    });
  }

  // trackBy da matriz/tabela: identidade estável por id → o Angular reusa as
  // linhas/células existentes em vez de destruir e recriar o DOM ao re-gerar.
  trackBySubmission = (_: number, s: SubmissionAssignSuggestions) => s.submission.id;
  trackByCommittee = (_: number, c: UserEvent) => c.user.id;
  trackByCommitteeUser = (_: number, u: UserAssignInfo) => u.userPreferences.user.id;


  // Monta a matriz GENÉRICA (submissões × comitês): cria UserAssignInfo, prepara
  // committee.reviewing (com o clone de revisões delegadas) e resolve elegibilidade
  // base (autor/conflito). NÃO calcula peso — isso é responsabilidade do algoritmo
  // (ver AssignmentAlgorithm.run), que também roda as fases de atribuição.
  buildMatrix(): Array<SubmissionAssignSuggestions> {
    const matrix = this.submissions.map(submission => {
      const submissionAssign = new SubmissionAssignSuggestions(submission);

      submissionAssign.possiblesAssign = this.committees.map(committee => {
        if (committee.reviewing === undefined) {
          committee.reviewing = [];
        }

        submission.reviews.filter(rv => ![ReviewStatus.DELETED, ReviewStatus.DECLINED].some(s => s === (<Review>rv).status))
        .map(r => {
          if((<Review>r).userId === committee.user.id) {
            committee.reviewing.push(submission.id);
          } else if ((<number>(<Review>r).assignedBy) === committee.user.id && !(<Review>r).isFirstAssign) {
            let AssignedReviewerName = ` (${(<User>(<Review>r).user).name})`;
            (<User>(<Review>r).user) = _.cloneDeep(committee.user);
            (<User>(<Review>r).user).lastName += AssignedReviewerName;
            committee.reviewing.push(submission.id);
          }
        });

        const userAssign = new UserAssignInfo(submission, committee);
        userAssign.submissionToAssign = submissionAssign;
        this.setEligibility(submission, userAssign);
        return userAssign;
      });

      return submissionAssign;
    });
    return matrix;
  }


  setEligibility(submission: Submission, user: UserAssignInfo): void {
    if (submission.isAuthor(user.userPreferences.user.id)) {
      user.elegible = false;
      user.motive = Motives.AUTHOR;
    } else if (submission.authors.some(author => user.userPreferences.hasConflict(author.user.id))) {
      user.elegible = false;
      user.motive = Motives.CONFLICT;
    } else {
      const review = <Review>submission.reviews.find(r => (<Review>r).userId === user.userPreferences.user.id || (<Review>r).assignedByUserId === user.userPreferences.user.id);
      if (review) {
        if (review.status === ReviewStatus.DECLINED) {
          user.elegible = false;
          user.motive = Motives.DECLINED;
        } else if (submission.hasClaim(user.userPreferences.user.id)) {
          user.elegible = false;
          user.motive = Motives.ASSIGNED_CLAIMED;
        } else {
          user.elegible = false;
          user.motive = Motives.ASSIGNED_INTEREST;
        }
      }
    }
  }

  initForm(): void {
    this.reviewsDueAt = this.reviewConfig.conferenceDateDue?.toDate();

    const form = this.fb.group({
      dueAt: [this.reviewsDueAt, [Validators.required]],
      timeZone: [this.tzService.browserTimeZone]
    });
    this.editableSlots.clear();

    this.matrix.forEach(submission => {
      const fbGroup = this.fb.group({});

      submission.suggestedAssign.forEach((userAssign, index) => {
        const key = index.toString();
        fbGroup.addControl(key, new FormControl(userAssign.userPreferences.user.id, []));
        submission.selectControls[key] = userAssign;
        // Sugestão NOVA (bid/interesse) desta rodada → editável; ASSIGNED_* (rodada
        // anterior) → travado. Fixado aqui, imune à troca de revisor depois.
        if (userAssign.motive === Motives.CLAIM || userAssign.motive === Motives.INTEREST) {
          this.editableSlots.add(`${submission.submission.id}:${key}`);
        }
      });

      const validReviews = submission.submission.reviews.filter(r => (<Review>r).status !== ReviewStatus.DECLINED);
      const actualReviews = submission.suggestedAssign.length + validReviews.length;
      for (let index = actualReviews; index < this.reviewConfig.numberOfReviewsRequired; index++) {
        const key = index.toString();
        fbGroup.addControl(key, new FormControl('', []));
        this.editableSlots.add(`${submission.submission.id}:${key}`);   // slot vazio → sempre editável
      }

      form.addControl(submission.submission.id.toString(), fbGroup);
    });

    this.form = form;
    this.assignmentsSaved = false;   // sugestões recém-geradas = pendentes até atribuir/descartar
    this.refreshSatisfaction();      // valor inicial (form já reflete as sugestões)

    this.form.get('dueAt')?.valueChanges.subscribe((date) =>{
      this.reviewsDueAt = date;
      this.cdr.markForCheck();
    });
  }

  getReviewersSuggested(submission: SubmissionAssignSuggestions) {
    const selectables = submission.submission.reviews.map(review => {
      if (this.isReview(review)) {
        // User already assigned
        return {
          assigned: true,
          user: <UserAssignInfo>{userPreferences: { user: typeof review !== 'number' ? review.user : undefined } }
        };
      } else {
        // Number of id of user suggested
        return {
          assigned: false,
          user: <UserAssignInfo>submission.possiblesAssign.find(c => c.userPreferences.user.id === review)
        };
      }
    });
    selectables.sort((a, b) => b.user.weight - a.user.weight);
    return selectables;
  }

  getSelectOptions(submission: SubmissionAssignSuggestions): Array<SelectOption> {
    if (submission.options === undefined) {
      const candidates = submission.possiblesAssign.slice()
        .sort((a, b) => b.weight - a.weight)
        .map(u => {          
          let userEventReviews = this.reviewStatusPerReviewerList.find(userReview => userReview.id === u.userPreferences.user.id);
          let placeholder = `[${u.weight}] ${u.userPreferences.user.name} (${userEventReviews.completed + userEventReviews.assignToOthersCompleted}/${userEventReviews.totalReviews}) `;
          if (u.userPreferences.user.profile.affiliation.acronym) {
            placeholder += `(${u.userPreferences.user.profile.affiliation.acronym})`;
          } 
          return new SelectOption(u.userPreferences.user.id, placeholder);
        });
      const options = [new SelectOption(undefined, `do not assign`), ...candidates];
      submission.options = options;
    }
    return submission.options;
  }

  isReview(review: Review | number): boolean {
    return !!review && typeof review !== 'number';
  }

  isCommittee(user: User | number): boolean {
    const userId = user instanceof User ? user.id : user;
    return this.committees.some(c => c.user.id === userId);
  }

  getWeightAssignedReview(submissionSuggestion: SubmissionAssignSuggestions, review: Review): UserAssignInfo {
    if (!review) {
      return undefined;
    } else {
      return submissionSuggestion.possiblesAssign.find(c => c.userPreferences.user.id === review.userId);
    }
  }

  getCommitteeSuggestedInfo(submissionSuggestion: SubmissionAssignSuggestions, assignControl: string): UserAssignInfo {
    const formGroup = this.form.get(submissionSuggestion.submission.id.toString());
    const selectControl = formGroup.get(assignControl);
    const committeeUserId = selectControl.value;

    return submissionSuggestion.possiblesAssign.find(c => c.userPreferences.user.id === committeeUserId);
  }

  getAssignControls(submission: SubmissionAssignSuggestions): Array<string> {
    const form = <FormGroup>this.form.get(submission.submission.id.toString());
    return Object.keys(form.controls);
  }

  updateSelectControl(submissionSuggestion: SubmissionAssignSuggestions, controlName, committeeUserId) {
    const userAssignInfo = submissionSuggestion.possiblesAssign.find( userAssign => userAssign.userPreferences.user.id === committeeUserId);
    submissionSuggestion.selectControls[controlName] = userAssignInfo;
    this.assignmentsSaved = false;   // editou um revisor → volta a haver pendência
    this.refreshSatisfaction();      // recalcula ao vivo com a troca manual
  }

  // Editabilidade FIXADA no initForm (ver editableSlots): slot vazio ou sugestão
  // nova (CLAIM/INTEREST) desta rodada = editável; rodada anterior (ASSIGNED_*) =
  // travado. NÃO deriva da motive do valor atual — senão trocar o revisor por um
  // candidato de outra motive travava o slot e o popover não reabria.
  isEditableReviewer(sub: SubmissionAssignSuggestions, controlName: string): boolean {
    return this.editableSlots.has(`${sub.submission.id}:${controlName}`);
  }

  reviewerAffiliation(info: UserAssignInfo): string {
    return info?.userPreferences?.user?.profile?.affiliation?.acronym || '';
  }

  // O slot contém a SUGESTÃO DO ALGORITMO? (valor atual == suggestedAssign[índice]).
  // Usado p/ colorir o badge da tabela SÓ quando é a sugestão — trocar o revisor
  // manualmente deixa o badge neutro (a cor sinaliza "sugestão do algoritmo", não
  // uma escolha manual). Consistente com o realce do badge no picker.
  isSlotSuggested(sub: SubmissionAssignSuggestions, control: string): boolean {
    const suggested = sub.suggestedAssign[Number(control)];
    const current = sub.selectControls[control];
    return !!suggested && !!current
      && suggested.userPreferences.user.id === current.userPreferences.user.id;
  }

  // --- Seletor de revisores leve (popover compartilhado sob demanda) ---------
  // Um clique na célula abre UM único app-popover (conteúdo lazy) ancorado nela,
  // com busca + lista rica (afinidade, nome, afiliação, carga de revisões). Sem
  // mat-select por célula (o vilão dos ~2,4s no load) e sem texto imitando combo.

  // Candidatos da submissão → linhas estruturadas p/ a lista, ordenados por
  // afinidade (weight) desc. A carga (concluídas/total) sai do reviewStatusList.
  private buildPickerCandidates(sub: SubmissionAssignSuggestions) {
    return sub.possiblesAssign
      .map(u => {
        const stats = this.reviewStatusPerReviewerList.find(r => r.id === u.userPreferences.user.id);
        return {
          id: u.userPreferences.user.id,
          name: u.userPreferences.user.name,
          affiliation: u.userPreferences.user.profile?.affiliation?.acronym || '',
          weight: u.weight,
          motive: u.motive,
          load: stats ? `${stats.completed + stats.assignToOthersCompleted}/${stats.totalReviews}` : '',
          loadTotal: stats ? stats.totalReviews : 0   // p/ desempate por carga
        };
      })
      // Ordem: 1) afinidade (weight) desc; 2) menor carga (total de revisões)
      // primeiro; 3) alfabético (pt, sem acento/caixa).
      .sort((a, b) =>
        (b.weight - a.weight)
        || (a.loadTotal - b.loadTotal)
        || a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }));
  }

  openReviewerPicker(sub: SubmissionAssignSuggestions, control: string, anchor: HTMLElement): void {
    if (!this.isEditableReviewer(sub, control) || !this.reviewerPicker) { return; }
    this.pickerContext = { sub, control };
    this.pickerSelectedId = this.form.get(sub.submission.id.toString())?.get(control)?.value || undefined;
    // Sugestão do algoritmo p/ ESTE slot = suggestedAssign[índice do slot] (o
    // control é o índice). Slots vazios (padding) não têm sugestão → undefined.
    this.pickerSuggestedId = sub.suggestedAssign[Number(control)]?.userPreferences.user.id;
    this.pickerCandidates = this.buildPickerCandidates(sub);
    this.pickerFilteredCandidates = this.pickerCandidates;
    this.reviewerPicker.anchor = anchor;   // seta ANTES de abrir (evita timing do @Input)
    this.reviewerPicker.show();
    this.cdr.markForCheck();
    // Foca a busca p/ filtrar digitando (o conteúdo já renderizou após show()).
    setTimeout(() => (document.querySelector('.reviewer-picker-search') as HTMLElement)?.focus());
  }

  filterPickerCandidates(term: string): void {
    const t = (term || '').trim().toLowerCase();
    this.pickerFilteredCandidates = !t
      ? this.pickerCandidates
      : this.pickerCandidates.filter(c =>
          c.name.toLowerCase().includes(t) || (!!c.affiliation && c.affiliation.toLowerCase().includes(t)));
    this.cdr.markForCheck();
  }

  chooseReviewer(candidate: { id: any } | undefined): void {
    if (!this.pickerContext) { return; }
    const { sub, control } = this.pickerContext;
    const id = candidate ? candidate.id : undefined;
    this.form.get(sub.submission.id.toString())?.get(control)?.setValue(id ?? '', { emitEvent: false });
    this.updateSelectControl(sub, control, id);   // atualiza selectControls + assignmentsSaved=false
    this.reviewerPicker?.hide();
    this.cdr.markForCheck();
  }

  setAssignReview(dueAt: Date) {
    const assignReviews = [];
    this.adminService.progressBar.start();
    this.submissions.forEach(submission => {
      const submissionControl = this.form.get(submission.id.toString());

      Object.keys(submissionControl.value)
        .filter(key => submissionControl.value[key])
        .forEach(selectKey => {
          const review = new AssignReview();
          review.assignedBy = this.infoService.user.id;
          review.submission = submission.id;
          review.user = submissionControl.value[selectKey];
          review.dueAt = dueAt;
          assignReviews.push(review);
      });
    });
    if(assignReviews.length>0){
      forkJoin(assignReviews.map(newReview => this.reviewsService.createReview(newReview))).subscribe(reviews => {
        this.notificationService.notify('admin.event.reviews.assign-suggestion.reviews-assigned', { params: { number: reviews.length } });
        this.assignmentsSaved = true;   // atribuições realizadas → saída liberada (guard não bloqueia)
        this.adminService.progressBar.stop();
      });
    }else{
      this.notificationService.notify('admin.event.reviews.assign-suggestion.reviews-already-assigned');
      this.adminService.progressBar.stop();
    }

  }

  submit() {
    const dueAt = this.form.get('dueAt').value;

    this.checkValidReview(dueAt, () => {
      this.setAssignReview(dueAt);
    });
  }

  checkValidReview(dueAt: Date, callback: () => void) {
    const dueAtPassed = dueAt < this.today();

    if (dueAtPassed) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'reviews.invalid-date-set',
          content: ''
        }
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) {
          callback();
        }
      });
    } else {
      callback();
    }
  }

  today(): Date {
    return new Date();
  }

  // --- Atribuições pendentes + guarda de saída ------------------------------
  // Uma "atribuição pendente" é um revisor sugerido/selecionado NESTA rodada que
  // ainda não foi realizado (persistido via "Atribuir revisores"). Enquanto
  // houver pendências, o usuário só sai da página se DESCARTAR (botão Descartar
  // ou o diálogo do guard) ou ATRIBUIR (submit). Espelha setAssignReview: pendente
  // = algum controle do form (por submissão) com valor de revisor preenchido.
  hasPendingAssignments(): boolean {
    if (!this.form) { return false; }
    return (this.submissions || []).some(submission => {
      const group = this.form.get(submission.id.toString());
      return !!group && Object.values(group.value).some(value => !!value);
    });
  }

  // Contrato do UnsavedChangesGuard (canDeactivate na rota). Bloqueia a saída
  // quando há atribuições pendentes ainda não realizadas.
  hasUnsavedChanges(): boolean {
    return !this.assignmentsSaved && this.hasPendingAssignments();
  }

  // Botão "Descartar": joga fora as atribuições sugeridas desta rodada (após
  // confirmar). Depois disso não há mais pendências → a saída é liberada.
  discard(): void {
    if (!this.hasPendingAssignments()) { return; }
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.reviews.assign-suggestion.discard-dialog.title',
        content: 'admin.event.reviews.assign-suggestion.discard-dialog.content',
        cancel: 'forms.buttons.cancel',
        confirm: 'forms.buttons.discard',
        destructive: true
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.clearPendingAssignments();
      }
    });
  }

  // Limpa as atribuições sugeridas NESTA rodada: zera os controles do form e
  // remove os selectControls de motivo CLAIM/INTEREST (o que a tabela mostra).
  // NÃO mexe nas revisões já atribuídas em rodadas anteriores (não estão no form).
  private clearPendingAssignments(): void {
    (this.submissions || []).forEach(submission => {
      const group = this.form?.get(submission.id.toString()) as FormGroup;
      if (group) {
        Object.keys(group.controls).forEach(key => group.get(key).setValue('', { emitEvent: false }));
      }
    });
    (this.matrix || []).forEach(sub => {
      Object.keys(sub.selectControls).forEach(key => {
        const info = sub.selectControls[key];
        if (info && (info.motive === Motives.CLAIM || info.motive === Motives.INTEREST)) {
          delete sub.selectControls[key];
        }
      });
    });
    this.reviewerPicker?.hide();
    this.refreshSatisfaction();   // recalcula após limpar as sugestões da rodada
    this.cdr.markForCheck();
  }
}
